import React, { useMemo } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { useToken, useColorMode } from "@chakra-ui/react";

// Try to resolve CSS var to computed color string
const resolveCssVar = (val) => {
  if (typeof window === "undefined") return val;
  if (!val || typeof val !== "string") return val;
  if (!val.startsWith("var(")) return val;
  const name = val.slice(4, -1).trim(); // -> --chakra-colors-xxx
  const got = getComputedStyle(document.documentElement).getPropertyValue(name);
  return got?.trim() || val; // if empty, return the var back (we'll fallback below)
};

// Provide safe fallbacks if a variable didn’t resolve yet
const withFallback = (val, light, dark, mode) => {
  const v = resolveCssVar(val);
  if (!v || typeof v !== "string" || v.startsWith("var(")) {
    return mode === "dark" ? dark : light;
  }
  return v;
};

export default function MuiBridge({ children }) {
  // Chakra semantic tokens (can be CSS vars)
  const raw = useToken("colors", [
    "appBg",     // 0
    "panel",     // 1
    "panelAlt",  // 2
    "brand",     // 3
    "text",      // 4
    "textMuted", // 5
    "danger",    // 6
  ]);
  const { colorMode } = useColorMode();

  // Final resolved colors with robust fallbacks
  const appBg     = withFallback(raw[0], "#f6f7fb", "#0e1525", colorMode);
  const panel     = withFallback(raw[1], "#ffffff", "#141c2f", colorMode);
  const panelAlt  = withFallback(raw[2], "#f1f4f9", "#101826", colorMode);
  const brand     = withFallback(raw[3], "#2d7ff9", "#2d7ff9", colorMode);
  const text      = withFallback(raw[4], "#1b2430", "#e9eef7", colorMode);
  const textMuted = withFallback(raw[5], "#4f5b6b", "#8ea0b5", colorMode);
  const danger    = withFallback(raw[6], "#d64545", "#e64848", colorMode);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: colorMode === "dark" ? "dark" : "light",
          primary: { main: brand },
          error:   { main: danger },
          background: { default: appBg, paper: panel },
          text: { primary: text, secondary: textMuted },
        },
        shape: { borderRadius: 12 },
        typography: { fontFamily: `'Segoe UI', system-ui, -apple-system, Arial, sans-serif` },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: { backgroundImage: "none", border: "1px solid rgba(0,0,0,0.06)" },
            },
          },
          MuiDataGrid: {
            styleOverrides: {
              root: { border: "1px solid rgba(255,255,255,.06)", backgroundColor: panel, color: text },
              columnHeaders: {
                backgroundColor: panelAlt,
                color: textMuted,
                fontWeight: 700,
                borderBottom: "1px solid rgba(255,255,255,.06)",
              },
              row: { "&:hover": { backgroundColor: "rgba(45,127,249,.08)" } },
            },
          },
          MuiButton: { styleOverrides: { root: { textTransform: "none", fontWeight: 700, borderRadius: 10 } } },
          MuiTablePagination: { styleOverrides: { root: { color: text } } },
          MuiOutlinedInput: {
            styleOverrides: {
              root: { backgroundColor: panelAlt },
              input: { paddingTop: 12, paddingBottom: 12, color: text },
            },
          },
          MuiSelect: { styleOverrides: { select: { backgroundColor: panelAlt, color: text } } },
        },
      }),
    [appBg, panel, panelAlt, brand, text, textMuted, danger, colorMode]
  );

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}
